import { useEffect } from 'react'
import { Button, Group, Modal, Stack, Switch, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zod4Resolver as zodResolver } from 'mantine-form-zod-resolver'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { obraEntrada, type Obra, type ObraEntrada } from '@serkat/shared'
import { ErrorApi, api } from '../../../api/cliente'

interface Props {
  abierto: boolean
  onCerrar: () => void
  /** Presente al editar desde el módulo; ausente al dar de alta. */
  inicial?: Obra | undefined
  /** Lo que el usuario ya escribió buscando la obra que no encontró. */
  nombreSugerido?: string | undefined
  /** Recibe la obra guardada, para que quien la pidió pueda seleccionarla. */
  onGuardada?: ((o: Obra) => void) | undefined
}

const VACIO: ObraEntrada = { codigo: '', nombre: '', ubicacion: '', activo: true }

/**
 * Alta y edición de obras. Vive aparte de la página porque lo usan dos sitios:
 * el módulo de Obras y el formulario de órdenes, cuando la obra no está en la
 * lista y no tiene sentido abandonar la orden a medias para ir a crearla.
 */
export function ModalObra({ abierto, onCerrar, inicial, nombreSugerido, onGuardada }: Props) {
  const qc = useQueryClient()

  const form = useForm<ObraEntrada>({
    initialValues: VACIO,
    validate: zodResolver(obraEntrada),
  })

  // Se recarga al abrir, no al montar: el modal permanece montado entre aperturas
  // y sin esto la segunda alta arrastraría lo escrito en la primera.
  useEffect(() => {
    if (!abierto) return
    form.setValues(
      inicial
        ? {
            codigo: inicial.codigo, nombre: inicial.nombre,
            ubicacion: inicial.ubicacion ?? '', activo: inicial.activo,
          }
        : { ...VACIO, nombre: nombreSugerido ?? '' },
    )
    form.resetDirty()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, inicial, nombreSugerido])

  const guardar = useMutation({
    mutationFn: (v: ObraEntrada) =>
      inicial ? api.patch<Obra>(`/obras/${inicial.id}`, v) : api.post<Obra>('/obras', v),
    onSuccess: (o) => {
      notifications.show({ color: 'teal', message: 'Obra guardada' })
      void qc.invalidateQueries({ queryKey: ['obras'] })
      // Los catálogos se cachean una hora. Sin invalidarlos, la obra recién
      // creada no aparecería en el selector que la pidió.
      void qc.invalidateQueries({ queryKey: ['catalogos'] })
      onGuardada?.(o)
      onCerrar()
    },
    onError: (e) =>
      notifications.show({
        color: 'red',
        title: 'No se pudo guardar',
        message: e instanceof ErrorApi ? e.message : 'Error inesperado',
      }),
  })

  return (
    <Modal
      opened={abierto}
      onClose={onCerrar}
      title={inicial ? 'Editar obra' : 'Nueva obra'}
      // Por encima del formulario de la orden, que también es un modal.
      zIndex={300}
    >
      <form onSubmit={form.onSubmit((v) => guardar.mutate(v))}>
        <Stack gap="sm">
          <TextInput label="Nombre" withAsterisk data-autofocus {...form.getInputProps('nombre')} />
          <TextInput
            label="Código"
            withAsterisk
            description="El que usa el área para esta obra. Se guarda en mayúsculas"
            placeholder="EST-MGRAU"
            {...form.getInputProps('codigo')}
          />
          <TextInput label="Ubicación" placeholder="Castilla, Piura" {...form.getInputProps('ubicacion')} />
          {inicial && (
            <Switch
              label="Activa"
              description="Las obras terminadas se desactivan: salen del selector y conservan su historial"
              {...form.getInputProps('activo', { type: 'checkbox' })}
            />
          )}
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={onCerrar}>Cancelar</Button>
            <Button type="submit" loading={guardar.isPending}>Guardar</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
